<template>
    <div class="flex mb-2">
        <Toolbar class="w-full">
            <template #end>
                <DatePicker v-model="selectedMonth" view="month" dateFormat="mm/yy" showIcon @date-select="onMonthChange" />
            </template>
        </Toolbar>
    </div>

    <div class="flex gap-2 pt-2.5">
        <Card class="flex-1" style="text-align: center">
            <template #content>
                <div class="font-bold text-2xl">R$ {{ formatMoney(financeiroInfos.total) }}</div>
                <div class="font-medium text-blue-500"><b>Total de Contas</b></div>
            </template>
        </Card>

        <Card class="flex-1" style="text-align: center">
            <template #content>
                <div class="font-bold text-2xl">R$ {{ formatMoney(financeiroInfos.pago) }}</div>
                <div class="font-medium text-green-500"><b>Total Pago</b></div>
            </template>
        </Card>

        <Card class="flex-1" style="text-align: center">
            <template #content>
                <div class="font-bold text-2xl">R$ {{ formatMoney(financeiroInfos.pendente) }}</div>
                <div class="font-medium text-red-500"><b>Total Pendente</b></div>
            </template>
        </Card>
    </div>

    <div class="flex gap-2 my-2">
        <Card class="flex-1" style="min-height: 400px;"> <template #title>
                <h4 class="m-0">PAGO vs PENDENTE (Mês Selecionado)</h4>
            </template>
            <template #content>
                <div style="max-width: 300px; margin: auto;"> <Chart v-if="_isMounted && paidPendingChartData?.labels?.length && paidPendingChartData?.datasets?.length" :key="`paid-${selectedMonth?.getTime?.() || 0}`" type="doughnut" :data="paidPendingChartData" :options="chartOptions" />
                </div>
            </template>
        </Card>

        <Card class="flex-1" style="min-height: 400px;"> <template #title>
                <h4 class="m-0">COMPARATIVO MENSAL</h4>
            </template>
            <template #content>
                <Chart v-if="_isMounted && monthlyFinancialChartData?.labels?.length && monthlyFinancialChartData?.datasets?.length" :key="`monthly-${selectedMonth?.getTime?.() || 0}`" type="bar" :data="monthlyFinancialChartData" :options="chartOptionsMultiAxis" />
            </template>
        </Card>
    </div>
</template>

<script>
import DatePicker from 'primevue/datepicker';
import SafeChart from '@/components/SafeChart.vue';
import Card from 'primevue/card';
import Toolbar from 'primevue/toolbar';

// Assumindo que você terá um serviço para as chamadas de API financeiras
import Conta from '@/service/conta.js';

import Mask from '@/utils/Mask.js';

export default {
    components: {
        DatePicker,
        Chart: SafeChart,
        Card,
        Toolbar,
    },
    data() {
        return {
            selectedMonth: new Date(), // Data inicial para o filtro, default para o mês atual
            financeiroInfos: {
                total: 0,
                pago: 0,
                pendente: 0,
            },
            paidPendingChartData: {}, // Dados para o gráfico de donut
            monthlyFinancialChartData: {}, // Dados para o gráfico de barra
            chartOptions: {
                responsive: true,
                plugins: {
                    legend: {
                        position: 'top',
                    },
                    tooltip: { // Adicione ou modifique a configuração do tooltip aqui
                        callbacks: {
                            label: function(context) {
                                let label = context.label || ''; // context.label é o rótulo da fatia (ex: 'Total Pago')

                                if (label) {
                                    label += ': ';
                                }
                                if (context.parsed !== null) { // Para donut, context.parsed é o valor numérico
                                    label += 'R$ ' + context.parsed.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                }
                                return label;
                            }
                        }
                    }
                },
            },
            chartOptionsMultiAxis: {
                responsive: true,
                plugins: {
                    legend: {
                        position: 'top',
                    },
                    tooltip: {
                        callbacks: {
                            label: function(context) {
                                let label = context.dataset.label || '';

                                if (label) {
                                    label += ': ';
                                }
                                if (context.parsed.y !== null) {
                                    label += 'R$ ' + context.parsed.y.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                }
                                return label;
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        stacked: false, // Mantém false para barras não empilhadas
                    },
                    y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Valor (R$)' // Título do eixo Y continua "Valor (R$)"
                        },
                    },
                }
            },

            _isMounted: false,
        };
    },
    mounted() {
        this._isMounted = true;
        this.onMonthChange(); // Carrega os dados iniciais ao montar o componente
    },
    beforeUnmount() {
        this._isMounted = false;
    },
    methods: {
        // Função chamada ao mudar o mês no DatePicker
        onMonthChange() {
            this.loadFinanceiroData();
            this.loadMonthlyFinancialChart();
        },

        // Carrega os totais de contas, pago e pendente para o mês selecionado
        async loadFinanceiroData() {
            try {
                const params = [`data=${this.selectedMonth.toISOString().split('T')[0]}`];

                const response = await Conta.valorTotal(params.join("&"));
                if (!this._isMounted) return;
                const data = response.data;

                this.financeiroInfos.total      = data.total;
                this.financeiroInfos.pago       = data.pago;
                this.financeiroInfos.pendente   = data.pendente;

                this.loadPaidPendingChart(data);

            } catch (error) {
                console.error("Erro ao carregar totais financeiros:", error);
            }
        },

        // Carrega os dados para o gráfico de donut (Total Pago x Total Pendente)
        async loadPaidPendingChart(data) {
            try {
                if (!this._isMounted) return;
                this.paidPendingChartData = {
                    labels: ['Total Pago', 'Total Pendente'],
                    datasets: [
                        {
                            data: [data.pago, data.pendente],
                            backgroundColor: ['#22C55E', '#EF4444'], // Verde para pago, Vermelho para pendente
                            hoverBackgroundColor: ['#16A34A', '#DC2626']
                        }
                    ]
                };
            } catch (error) {
                console.error("Erro ao carregar dados do gráfico de pago/pendente:", error);
            }
        },

        // Carrega os dados para o gráfico de barra (Total Pago últimos 3 meses e Total Contas mensal)
        async loadMonthlyFinancialChart() {
            try {
                const params = [`data=${this.selectedMonth.toISOString().split('T')[0]}`];

                const response  = await Conta.valorMensal(params.join("&"));
                if (!this._isMounted) return;
                const data      = response.data;

                this.monthlyFinancialChartData = {
                    labels: data.labels,
                    datasets: [
                        {
                            type: 'bar',
                            label: 'Valor Total Pago', // Rótulo para as COLUNAS
                            backgroundColor: '#3B82F6',
                            data: data.pago, // Dados de "Total Pago" para as barras
                            yAxisID: 'y', // Aponta para o eixo Y principal
                        },
                        {
                            type: 'line',
                            label: 'Valor Total de Contas', // Rótulo para a LINHA
                            borderColor: '#EC4899',
                            borderWidth: 2,
                            fill: false,
                            data: data.total, // Dados de "Valor Total de Contas" para a linha
                            tension: 0.4,
                            yAxisID: 'y', // Aponta para o eixo Y principal
                        }
                    ]
                };
            } catch (error) {
                console.error("Erro ao carregar dados do gráfico financeiro mensal:", error);
            }
        },

        formatMoney(valor) {
            if (!valor) return Mask.money(0);
            return Mask.money(valor);
        },
    },
};
</script>

<style scoped>
/* Adicione estilos específicos aqui se necessário */
</style>
